// src/pages/ListarCobrancasPix.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Button, TextField, MenuItem,
  Tooltip, Chip, IconButton, Stack, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Card, CardContent, InputAdornment, TablePagination, Link
} from "@mui/material";
import {
  ContentCopy, OpenInNew, FileDownload, Info, Today, RestartAlt, WhatsApp, Search
} from "@mui/icons-material";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
  BarChart, Bar, Legend, CartesianGrid, Cell
} from "recharts";
import { alpha } from "@mui/material/styles";
import api from "../services/api";
import confetti from "canvas-confetti";
import { Link as RouterLink } from 'react-router-dom';
import SearchUserAutocomplete from "../components/SearchUserAutocomplete";

const toLicenca = (chave) => `/licencas/novo/${encodeURIComponent(chave)}`;

// 4 blocos de 4 caracteres A–Z/0–9: XXXX-XXXX-XXXX-XXXX
const LICENSE_RE = /\b[A-Z0-9]{4}(?:-[A-Z0-9]{4}){3}\b/i;

// Pega a chave seja no campo dedicado ou "escondida" na descrição
const getChaveLicenca = (c) => {
  if (c.chave_licenca) return String(c.chave_licenca).toUpperCase();
  if (c.licenca_chave) return String(c.licenca_chave).toUpperCase();
  const m = (c.descricao || "").match(LICENSE_RE);
  return m ? m[0].toUpperCase() : null;
};

// Link reutilizável (abre em nova aba e não dispara o modal da linha)
function LicenseLink({ chave }) {
  if (!chave) return null;
  return (
    <Link
      component={RouterLink}
      to={toLicenca(chave)}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      onClick={(e)=>e.stopPropagation()}
      onMouseDown={(e)=>e.stopPropagation()}
      sx={{ fontFamily:'ui-monospace,Menlo,monospace', fontWeight:600 }}
    >
      {chave}
    </Link>
  );
}

/* ================= helpers (normalização e datas) ================= */
const norm = (s) =>
  (s ?? "").toString().toLowerCase()
    .normalize("NFD").replace(/\p{Diacritic}/gu, "");

// dd/mm/aaaa HH:mm (timezone São Paulo)
const fmtBRDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", hour12: false });
};

/* ================= Paleta/labels de status ================= */
const statusVisual = {
  pending:   { bg: "#fb8c00", fg: "#fff", label: "pendente"   },
  approved:  { bg: "#43a047", fg: "#fff", label: "aprovado"   },
  expired:   { bg: "#757575", fg: "#fff", label: "expirado"   },
  cancelled: { bg: "#e53935", fg: "#fff", label: "cancelado"  },
};

/* ===== linha colorida como os cards ===== */
const rowSxByStatus = (status) => {
  const key = String(status || "").toLowerCase();
  const color = statusVisual[key]?.bg;
  if (!color) return {};
  return {
    background: `linear-gradient(90deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.04)} 60%, transparent 100%)`,
    "&:hover": { backgroundColor: alpha(color, 0.12) },
    "& td, & th": { borderBottomColor: alpha(color, 0.25) },
    "& td:first-of-type": { borderLeft: `4px solid ${color}` },
    cursor: "pointer",
  };
};

/* ================= utils preservados ================= */
function isHoje(dataIso) {
  if (!dataIso) return false;
  const hoje = new Date();
  const d = new Date(dataIso);
  return d.getDate() === hoje.getDate() && d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear();
}

function NovoBadge({ children }) {
  return (
    <span style={{ position: "relative", marginLeft: 4, display: "inline-block" }}>
      <span
        style={{
          animation: "pulse 1s infinite",
          background: "#43a047",
          color: "#fff",
          borderRadius: 6,
          padding: "0 6px",
          fontSize: 10,
          position: "absolute",
          top: -14,
          left: 0,
          fontWeight: 700,
          boxShadow: "0 0 3px 1px #43a04744"
        }}
      >
        Novo
      </span>
      {children}
      <style>{`
        @keyframes pulse { 0% {opacity:1;transform:scale(1)} 50% {opacity:.4;transform:scale(1.1)} 100% {opacity:1;transform:scale(1)} }
      `}</style>
    </span>
  );
}

function getFaturamentoExtra(cobrancas) {
  let totalHoje = 0, totalMes = 0;
  const hoje = new Date();
  const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  cobrancas.forEach(c => {
    if (c.status !== "approved") return;
    const data = new Date(c.data_criacao);
    const val = parseFloat(c.valor || 0);
    if (
      data.getDate() === hoje.getDate() &&
      data.getMonth() === hoje.getMonth() &&
      data.getFullYear() === hoje.getFullYear()
    ) {
      totalHoje += val;
    }
    if (data >= primeiroDiaMes && data <= hoje) totalMes += val;
  });
  return { totalHoje, totalMes };
}

function useConfetti(busca) {
  useEffect(() => {
    if (!busca) return;
    const termo = busca.trim().toLowerCase();
    if (termo.includes("show") || termo.includes("parabéns")) {
      confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 } });
    }
  }, [busca]);
}

/* ================= Constantes de paginação ================= */
const PAGE_SIZES = [50, 100, 200, 500, 1000];

export default function ListarCobrancasPix() {
  // dados da PÁGINA ATUAL
  const [cobrancas, setCobrancas] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalFaturado, setTotalFaturado] = useState(0);

  const [filtros, setFiltros] = useState({ status: "", dataIni: "", dataFim: "", busca: "" });
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [graficoDias, setGraficoDias] = useState([]);
  const [graficoStatus, setGraficoStatus] = useState([]);

  // paginação
  const [page, setPage] = useState(0);            // 0-based no front
  const [pageSize, setPageSize] = useState(50);   // default

  const isAdmin = (localStorage.getItem("tipo") === "admin");
  const [usuarioSel, setUsuarioSel] = useState(null); // { nome, chave }

  // extras (com base na página atual)
  const { totalHoje, totalMes } = getFaturamentoExtra(cobrancas);
  useConfetti(filtros.busca);

  /* ================= Busca forte (client-side na PÁGINA) ================= */
  const filtradas = useMemo(() => {
    const q = norm(filtros.busca);
    if (!q) return cobrancas;

    return cobrancas.filter(c => {
      const statusLabel = statusVisual[c.status]?.label || c.status;
      const lic = getChaveLicenca(c);
      const alvo = [
        c.id, c.cliente_nome, c.usuario_nome, c.descricao, c.copiaecola,
        c.status, statusLabel,
        lic,
        c.data_criacao, c.data_pagamento, c.data_expiracao,
        fmtBRDateTime(c.data_criacao), fmtBRDateTime(c.data_pagamento), fmtBRDateTime(c.data_expiracao),
        parseFloat(c.valor || 0).toFixed(2).replace(".", ",")
      ].filter(Boolean).join(" ");
      return norm(alvo).includes(q);
    });
  }, [cobrancas, filtros.busca]);

  /* ================= Gráficos (com base na página atual) ================= */
  function processaGraficoDias(itens) {
    const mapa = {};
    itens.forEach(c => {
      if (!c.data_criacao) return;
      const dia = c.data_criacao.slice(0, 10);
      if (!mapa[dia]) mapa[dia] = { dia, faturado: 0, aprovadas: 0, pendentes: 0, total: 0 };
      mapa[dia].total += 1;
      if (c.status === "approved") {
        mapa[dia].faturado += parseFloat(c.valor || 0);
        mapa[dia].aprovadas += 1;
      }
      if (c.status === "pending") mapa[dia].pendentes += 1;
    });
    return Object.values(mapa).sort((a, b) => a.dia.localeCompare(b.dia));
  }

  function processaGraficoStatus(itens) {
    const mapa = {};
    itens.forEach(c => {
      if (!c.status) return;
      if (!mapa[c.status]) mapa[c.status] = { status: c.status, quantidade: 0, valor: 0 };
      mapa[c.status].quantidade += 1;
      mapa[c.status].valor += parseFloat(c.valor || 0);
    });
    return Object.values(mapa);
  }

  /* ================= Fetch (compatível com backend antigo e novo) ================= */
  const fetchCobrancas = async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, pageSize };
      if (filtros.status)  params.status   = filtros.status;
      if (filtros.dataIni) params.data_ini = filtros.dataIni;
      if (filtros.dataFim) params.data_fim = filtros.dataFim;
      if (isAdmin && usuarioSel?.chave) params.usuario = usuarioSel.chave;

      const { data } = await api.get("/listar-cobrancas-Pix", { params });

      // novo formato paginado
      if (Array.isArray(data?.rows)) {
        setCobrancas(data.rows);
        setTotalCount(Number(data.total) || 0);
        setTotalFaturado(Number(data.totalFaturado || 0));
        setGraficoDias(processaGraficoDias(data.rows));
        setGraficoStatus(processaGraficoStatus(data.rows));
        return;
      }

      // formato antigo (sem paginação) — mostramos como 1 página
      const lista = data?.cobrancas || [];
      setCobrancas(lista);
      setTotalCount(lista.length);
      setTotalFaturado(Number(data?.totalFaturado || 0));
      setGraficoDias(processaGraficoDias(lista));
      setGraficoStatus(processaGraficoStatus(lista));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCobrancas(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (isAdmin) { setPage(0); fetchCobrancas(); } /* eslint-disable-next-line */ }, [usuarioSel]);
  useEffect(() => { fetchCobrancas(); /* eslint-disable-next-line */ }, [page, pageSize]);

  const handleCopy = (txt) => navigator.clipboard.writeText(String(txt));

  const exportarCSV = () => {
    // CSV da PÁGINA
    const header = ["ID","Data","Valor","Status","Cliente","Descrição","Usuário","Instituição","Licença"].join(";");
    const rows = cobrancas.map(c =>
      [c.id, c.data_criacao, c.valor, c.status, c.cliente_nome, c.descricao, c.usuario_nome, c.instituicao, getChaveLicenca(c)]
        .map(x => `"${x || ""}"`).join(";")
    );

    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cobrancas_pix_p${page+1}_n${pageSize}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // datas rápidas
  const setHojeQuick = () => {
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = String(hoje.getDate()).padStart(2, "0");
    const hojeStr = `${yyyy}-${mm}-${dd}`;
    setFiltros(f => ({ ...f, dataIni: hojeStr, dataFim: hojeStr }));
  };

  const setTrintaDiasQuick = () => {
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const dd = String(hoje.getDate()).padStart(2, "0");
    const hojeStr = `${yyyy}-${mm}-${dd}`;
    const antes = new Date(); antes.setDate(hoje.getDate() - 29);
    const yyyyA = antes.getFullYear();
    const mmA = String(antes.getMonth() + 1).padStart(2, "0");
    const ddA = String(antes.getDate()).padStart(2, "0");
    const trintaDiasStr = `${yyyyA}-${mmA}-${ddA}`;
    setFiltros(f => ({ ...f, dataIni: trintaDiasStr, dataFim: hojeStr }));
  };

  const limparFiltros = () => {
    setFiltros({ status: "", dataIni: "", dataFim: "", busca: "" });
    setUsuarioSel(null);
    setPage(0);
    fetchCobrancas();
  };

  const statusResumo = useMemo(() => {
    const mapa = {};
    cobrancas.forEach(c => {
      if (!c.status) return;
      if (!mapa[c.status]) mapa[c.status] = { quantidade: 0, valor: 0 };
      mapa[c.status].quantidade += 1;
      mapa[c.status].valor += parseFloat(c.valor || 0);
    });
    return mapa;
  }, [cobrancas]);

  const handleFiltrarClick = () => { setPage(0); fetchCobrancas(); };

  const grafStatusData = useMemo(() => processaGraficoStatus(cobrancas), [cobrancas]);

  return (
    <Box p={{ xs: 1, md: 2 }} maxWidth="1300px" width="100%" mx="auto">
      <Typography variant="h5" mb={2} fontWeight={700}>Cobranças Pix</Typography>

      {/* ===== TOPO (duas linhas) ===== */}
      <Paper sx={{ p: 1, mb: 2, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {isAdmin && (
            <>
              <SearchUserAutocomplete
                value={usuarioSel}
                onChange={(u) => { setUsuarioSel(u); setPage(0); fetchCobrancas(); }}
                sx={{ mr: 1 }}
              />
              <Button size="small" onClick={() => { setUsuarioSel(null); setPage(0); fetchCobrancas(); }}>
                Ver todos
              </Button>
            </>
          )}

          <TextField
            label="Status"
            select
            size="small"
            value={filtros.status}
            onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.keys(statusVisual).map(k =>
              <MenuItem key={k} value={k}>{statusVisual[k].label}</MenuItem>
            )}
          </TextField>

          <TextField
            label="De"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtros.dataIni}
            onChange={e => setFiltros(f => ({ ...f, dataIni: e.target.value }))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          />

          <TextField
            label="Até"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtros.dataFim}
            onChange={e => setFiltros(f => ({ ...f, dataFim: e.target.value }))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          />

          <TextField
            label="Busca"
            size="small"
            placeholder="Nome, ID, descrição, status…"
            value={filtros.busca}
            onChange={e => setFiltros(f => ({ ...f, busca: e.target.value }))}
            sx={{ flex: "1 1 260px", minWidth: 260 }}
            InputProps={{
              sx: { height: 34, fontSize: 14 },
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            }}
          />
        </Box>

        {/* LINHA 2: HOJE/30 DIAS à esquerda — ações à direita */}
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button startIcon={<Today />} variant="outlined" size="small" onClick={setHojeQuick} sx={{ px: 1, whiteSpace: "nowrap" }}>
              HOJE
            </Button>
            <Button startIcon={<Today />} variant="outlined" size="small" onClick={setTrintaDiasQuick} sx={{ px: 1, whiteSpace: "nowrap" }}>
              30 DIAS
            </Button>
          </Box>

          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleFiltrarClick} disabled={loading} sx={{ px: 2, whiteSpace: "nowrap" }}>
              {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null} FILTRAR
            </Button>
            <Button startIcon={<RestartAlt />} variant="outlined" size="small" onClick={limparFiltros} sx={{ px: 1, minWidth: 0, whiteSpace: "nowrap" }}>
              LIMPAR
            </Button>
            <Button startIcon={<FileDownload />} variant="outlined" size="small" onClick={exportarCSV} sx={{ px: 1, minWidth: 0, whiteSpace: "nowrap" }}>
              CSV (página)
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* CARDS resumo (da página atual) */}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
        {["pending", "approved", "expired", "cancelled"].map(st => (
          <Grid item key={st} xs={12} sm={6} md={3}>
            <Card
              onClick={() => { setFiltros(f => ({ ...f, status: st })); setPage(0); setTimeout(fetchCobrancas, 0); }}
              sx={{
                cursor: 'pointer',
                width: '100%',
                height: '100%',
                borderRadius: 2,
                boxShadow: 1,
                background: `linear-gradient(180deg, ${statusVisual[st]?.bg}22, #fff)`,
                borderTop: `5px solid ${statusVisual[st]?.bg}`,
                transition: 'transform .06s ease',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="overline" sx={{ color: statusVisual[st]?.bg, fontWeight: 700 }}>
                  {statusVisual[st]?.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  {statusResumo[st]?.quantidade || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">cobranças (página)</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* RESUMO e GRÁFICOS */}
      <Stack direction={{ xs: "column", md: "row" }} gap={2} mb={3}>
        {/* Total faturado */}
        <Paper sx={{ flex: 1, p: 2, minWidth: 260 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Total faturado (backend ou página)</Typography>
          <Typography variant="h5" color="success.main" fontWeight={800}>
            R$ {parseFloat(totalFaturado || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
          </Typography>
          <Typography variant="body2" sx={{ color: "#388e3c", mt: 1, fontWeight: 600 }}>
            Hoje: R$ {parseFloat(totalHoje).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} <br />
            <span style={{ color: "#1e88e5" }}>
              Mês: R$ {parseFloat(totalMes).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </span>
          </Typography>
          <Typography variant="body2" sx={{ color: "#888", mt: 1 }}>
            Mostrando <b>{filtradas.length}</b> de <b>{cobrancas.length}</b> na página
          </Typography>
        </Paper>

        {/* Gráfico Faturamento por Dia */}
        <Paper sx={{ flex: 2, p: 2, minWidth: 350 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Faturamento diário (página)</Typography>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={graficoDias}>
              <XAxis dataKey="dia" fontSize={12} />
              <YAxis fontSize={12} />
              <ChartTooltip formatter={(v) => `R$ ${parseFloat(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
              <Line type="monotone" dataKey="faturado"   name="Faturado"   stroke="#43a047" strokeWidth={2} dot />
              <Line type="monotone" dataKey="aprovadas"  name="Aprovadas"  stroke="#1565c0" strokeDasharray="3 3" dot={false} />
              <Line type="monotone" dataKey="pendentes"  name="Pendentes"  stroke="#fb8c00" strokeDasharray="3 3" dot={false} />
              <CartesianGrid strokeDasharray="3 3" />
              <Legend />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Gráfico por Status */}
        <Paper sx={{ flex: 1.2, p: 2, minWidth: 260 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Cobranças por status (página)</Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={grafStatusData}>
              <XAxis dataKey="status" fontSize={12} tickFormatter={(s) => statusVisual[s]?.label || s} />
              <YAxis fontSize={12} />
              <ChartTooltip />
              <Bar dataKey="quantidade" name="Quantidade" radius={[6,6,0,0]}>
                {grafStatusData.map((d) => (
                  <Cell key={`q-${d.status}`} fill={statusVisual[d.status]?.bg || "#90caf9"} />
                ))}
              </Bar>
              <Bar dataKey="valor" name="Valor (R$)" radius={[6,6,0,0]}>
                {grafStatusData.map((d) => (
                  <Cell key={`v-${d.status}`} fill={statusVisual[d.status]?.bg || "#a5d6a7"} fillOpacity={0.45} />
                ))}
              </Bar>
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Stack>

      {/* TABELA PRINCIPAL */}
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ bgcolor: "#fafafa" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Pix</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtradas.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: "#888", py: 4 }}>
                  Nenhuma cobrança encontrada.
                </TableCell>
              </TableRow>
            )}

            {filtradas.map((c) => (
              <TableRow
                key={c.id}
                hover
                onClick={() => setShowDetails(c)}
                sx={rowSxByStatus(c.status)}
              >
                <TableCell>
                  <Tooltip title={fmtBRDateTime(c.data_criacao)}>
                    <span>
                      {fmtBRDateTime(c.data_criacao) || "-"}
                      {isHoje(c.data_criacao) && <NovoBadge />}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <Tooltip title="Copiar ID">
                    <IconButton size="small" onClick={(e)=>{e.stopPropagation(); handleCopy(c.id);}}>
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  {c.id}
                </TableCell>

                {/* COLUNA CLIENTE */}
                <TableCell onClick={(e)=>e.stopPropagation()} onMouseDown={(e)=>e.stopPropagation()}>
                  <Box sx={{ maxWidth: 260 }}>
                    {/* chave em cima (link) */}
                    {(() => {
                      const lic = getChaveLicenca(c);
                      return lic ? <LicenseLink chave={lic} /> : null;
                    })()}

                    {/* nome embaixo + WhatsApp opcional */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {c.cliente_nome || <i style={{ color: "#aaa" }}>-</i>}
                      </Typography>
                      {c.cliente_telefone && (
                        <Tooltip title="WhatsApp">
                          <IconButton
                            size="small"
                            color="success"
                            href={`https://wa.me/${c.cliente_telefone.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e)=>e.stopPropagation()}
                          >
                            <WhatsApp fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <b>R$ {parseFloat(c.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</b>
                </TableCell>

                <TableCell>
                  <Tooltip title={statusVisual[c.status]?.label || c.status}>
                    <Chip
                      label={statusVisual[c.status]?.label || c.status}
                      size="small"
                      sx={{
                        bgcolor: statusVisual[c.status]?.bg || "#e0e0e0",
                        color: statusVisual[c.status]?.fg || "#333",
                        fontWeight: 700,
                        minWidth: 92
                      }}
                      icon={c.status === "pending" ? <Info fontSize="small" /> : undefined}
                    />
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <Tooltip title={c.descricao || ""}>
                    <span>
                      {c.descricao?.slice(0, 32)}
                      {c.descricao?.length > 32 ? "..." : ""}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell>{c.usuario_nome || <i style={{ color: "#aaa" }}>-</i>}</TableCell>

                <TableCell>
                  {c.copiaecola && (
                    <Tooltip title="Copiar código Pix">
                      <IconButton size="small" onClick={(e)=>{e.stopPropagation(); handleCopy(c.copiaecola);}}>
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell onClick={(e)=>e.stopPropagation()}>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Ver detalhes">
                      <IconButton size="small" onClick={() => setShowDetails(c)}>
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {c.ticket_url && (
                      <Tooltip title="Abrir ticket">
                        <IconButton
                          size="small"
                          href={c.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e)=>e.stopPropagation()}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={PAGE_SIZES}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          labelRowsPerPage="Linhas por página:"
        />
      </Paper>

      {/* Modal de detalhes */}
      <Dialog open={!!showDetails} onClose={() => setShowDetails(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes da Cobrança #{showDetails?.id}</DialogTitle>
        <DialogContent dividers>
          {showDetails && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Status:&nbsp;
                <Chip
                  label={statusVisual[showDetails.status]?.label || showDetails.status}
                  size="small"
                  sx={{
                    bgcolor: statusVisual[showDetails.status]?.bg || "#e0e0e0",
                    color: statusVisual[showDetails.status]?.fg || "#333",
                    fontWeight: 700
                  }}
                />
              </Typography>

              <Typography variant="body2" gutterBottom>
                Valor: <b>R$ {parseFloat(showDetails.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</b>
              </Typography>

              <Typography variant="body2">Data criação: <b>{fmtBRDateTime(showDetails.data_criacao)}</b></Typography>
              <Typography variant="body2">Data pagamento: <b>{showDetails.data_pagamento ? fmtBRDateTime(showDetails.data_pagamento) : "-"}</b></Typography>
              <Typography variant="body2">Data expiração: <b>{showDetails.data_expiracao ? fmtBRDateTime(showDetails.data_expiracao) : "-"}</b></Typography>
              <Typography variant="body2">Cliente: <b>{showDetails.cliente_nome || "-"}</b></Typography>
              <Typography variant="body2">Descrição: <b>{showDetails.descricao || "-"}</b></Typography>
              <Typography variant="body2">Usuário: <b>{showDetails.usuario_nome || "-"}</b></Typography>
              <Typography variant="body2">Instituição: <b>{showDetails.instituicao || "-"}</b></Typography>
              <Typography variant="body2">Chave Licença: <b>{showDetails.chave_licenca || "-"}</b></Typography>
              <Typography variant="body2" gutterBottom>Chave Usuário: <b>{showDetails.chave_usuario || "-"}</b></Typography>

              <Typography variant="body2">Copia e Cola Pix:</Typography>
              <Paper sx={{ bgcolor: "#fafafa", p: 1, wordBreak: "break-all", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <span style={{ flex: 1, fontSize: 14 }}>{showDetails.copiaecola}</span>
                  <Tooltip title="Copiar código Pix">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(String(showDetails.copiaecola || ""))}>
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
